import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/query/react";
import { db } from "@/firebase";
import { addDoc, collection, deleteDoc, doc, getDoc, getDocs, updateDoc, query, where, limit } from "firebase/firestore";
import { addLoadingTask, deleteLoadingTask } from "@/store/uiSlice";
import { RootState } from "@/store/store";
import { BaseElement, NewElement, Template, WebsiteElement } from "@/types";
import { pick } from "lodash";

export const newtemplatesApi = createApi({
  reducerPath: "newTemplates",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["templates", "elements"],
  endpoints: (builder) => ({
    fetchTemplateById: builder.query({
      queryFn: async (templateId: string | SkipToken, { dispatch }) => {
        const TASK = "Plantilla";
        try {
          dispatch(addLoadingTask(TASK));
          const docRef = doc(db, `templates/${String(templateId)}`);
          const snapshot = await getDoc(docRef);
          const response = {
            id: templateId,
            ...snapshot.data(),
          };
          return { data: response as Template };
        } catch (error) {
          console.error(error);
          throw error;
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      providesTags: ["templates"],
    }),
    updateTemplateStyle: builder.mutation({
      queryFn: async ({ templateId, styles }: { templateId: Template["id"]; styles: string }, { getState, dispatch }) => {
        const store = getState() as RootState;
        const TASK = "Actualizar estilos de plantilla";
        try {
          dispatch(addLoadingTask(TASK));

          if (!store.auth?.user?.uid || !templateId || !styles) {
            throw new Error("No template available");
          }

          const docRef = doc(db, `templates/${String(templateId)}`);

          const payload = {
            styles,
            lastUpdatedBy: store.auth.user.uid,
            lastUpdatedAt: Date.now(),
          };
          const response = await updateDoc(docRef, payload);

          return { data: response };
        } catch (error) {
          return { error };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["templates"],
    }),
    updateTemplate: builder.mutation<BaseElement, { templateId: string; data: string } | SkipToken>({
      queryFn: async ({ templateId, data }, { getState }) => {
        const { auth } = getState() as RootState;

        if (!auth?.user?.uid) {
          return {
            error: {
              status: 401,
              statusText: "No autorizado",
            },
          };
        }

        const docRef = doc(db, `templates/${String(templateId)}`);

        try {
          await updateDoc(docRef, {
            data,
          });

          return {
            data,
          };
        } catch {
          return {
            error: {
              status: 500,
              statusText: "Error al actualizar",
            },
          };
        }
      },
      invalidatesTags: ["templates"],
    }),
    fetchElements: builder.query({
      queryFn: async (templateId: string | SkipToken) => {
        const colRef = collection(db, `templates/${String(templateId)}/elements`);

        const snapshot = await getDocs(colRef);

        const response = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        return {
          data: response as WebsiteElement[],
        };
      },
      providesTags: ["elements"],
    }),
    updateElement: builder.mutation<BaseElement, { templateId: string; payload: WebsiteElement } | SkipToken>({
      queryFn: async ({ templateId, payload }, { getState }) => {
        const { auth } = getState() as RootState;

        if (!auth?.user?.uid) {
          return {
            error: {
              status: 401,
              statusText: "No autorizado",
            },
          };
        }
        
        try {
          const docRef = doc(db, `templates/${String(templateId)}/elements/${String(payload?.id)}`);

          const ALLOWED_FIELD = [
            "data",
            "position",
            'width',
            "styles",
            "url",
            "type",
            "title",
            "lastUpdatedBy",
            "lastUpdatedAt",
          ];
          
          const newPayload: Partial<BaseElement> = {
            ...pick(payload, ALLOWED_FIELD),
            lastUpdatedBy: auth.user.uid,
            lastUpdatedAt: Date.now(),
          }

          await updateDoc(docRef, newPayload);

          return {
            data: newPayload as BaseElement
          };
        } catch {
          return {
            error: {
              status: 500,
              statusText: "Error al actualizar",
            },
          };
        }
      },
      invalidatesTags: ["elements"],
    }),
    duplicateElement: builder.mutation<BaseElement, { templateId: string; payload: WebsiteElement } | SkipToken>({
      queryFn: async({templateId, payload}, {getState}) => {
        const {auth} = getState() as RootState;

        if (!auth?.user?.uid) {
          return {
            error: {
              status: 401,
              statusText: "No autorizado",
            }
          }
        }


        try {
          const collectionRef = collection(db, `templates/${String(templateId)}/elements`);
          let ALLOWED_FIELDS: string[] = [];
          const BASE_FIELDS = [
            "title",
            "type",
            "data",
            "styles",
          ];

          switch (payload.type) {
            case 'drawer':
              ALLOWED_FIELDS = [...BASE_FIELDS, "position", "width"];
              break;
            case 'page':
              ALLOWED_FIELDS = [...BASE_FIELDS, "url"];
              break;
          }

          const newElement: Partial<NewElement> = {
            ...pick(payload, ALLOWED_FIELDS),
            title: payload.title.trim() + " (copia)",
            createdBy: auth.user.uid,
            createdAt: Date.now(),
          }

           const doc = await addDoc(collectionRef, newElement);

          return {
            data: {
              ...newElement,
              id: doc.id,
            } as BaseElement,
          };
        } catch {
          return {
            error: {
              status: 500,
              statusText: "Error al duplicar",
            },
          };
        }

      },
      invalidatesTags: ["elements"]
    }),
    deleteElement: builder.mutation({
      queryFn: async({templateId, elementId}, {getState}) => {
        const {auth} = getState() as RootState;
        if (!auth?.user?.uid) {
          return {
            error: {
              status: 401,
              statusText: "No autorizado",
            },
          };
        }
        
        const docRef = doc(db, `templates/${String(templateId)}/elements/${String(elementId)}`);

        try {
          const response = await deleteDoc(docRef);

          return {
            data: response,
          };
        } catch {
          return {
            error: {
              status: 500,
              statusText: "Error al eliminar",
            },
          };
        }
      },
      invalidatesTags: ["elements"],
    }),
    fetchTemplatesByIndustry: builder.query<Template[], string | SkipToken>({
      queryFn: async (industry) => {
        try {
          if (!industry) {
            return { data: [] };
          }
          const colRef = collection(db, "templates");
          const q = query(
            colRef,
            where("isPublished", "==", true),
            where("isPrivate", "==", false),
            where("type", "==", "web"),
            where("industry", "array-contains", industry),
            limit(10),
          );
          const snapshot = await getDocs(q);
          const response = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          return { data: response as Template[] };
        } catch (error) {
          return { error };
        }
      },
      providesTags: ["templates"],
    })

  }),
});

export const {
  useFetchTemplateByIdQuery,
  useLazyFetchTemplateByIdQuery,
  useFetchElementsQuery,
  useLazyFetchElementsQuery,
  useUpdateTemplateStyleMutation,
  useUpdateTemplateMutation,
  useUpdateElementMutation,
  useDuplicateElementMutation,
  useDeleteElementMutation,
  useFetchTemplatesByIndustryQuery,
} = newtemplatesApi;
